import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { buildApiConfig } from '../services/api/apiClient';
import { endPointEnums } from '../services/api/endpoints';
import useApiCall from '../services/api/useApiCall';

/**
 * Custom hook for pricing operations
 * Fetches active pricing rules and provides price calculation
 *
 * Supports:
 * - V1 pricing (legacy)
 * - V2 pricing (individual users)
 * - V3 pricing (business users with contracts)
 *
 * Auto-routes to V3 for BUSINESS users with active contracts
 */
export default function usePricing() {
  const [activePricing, setActivePricing] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  const [activeContract, setActiveContract] = useState(null);
  const [contractLoaded, setContractLoaded] = useState(false);

  // Get user type from Redux store
  const userType = useSelector(
    (state) => state.userProfile?.userType || state.auth?.user?.userType || 'INDIVIDUAL'
  );
  const isBusinessUser = userType === 'BUSINESS';

  const { makeApiCall, loading: loadingActivePricing, error: activePricingError } = useApiCall();

  const { makeApiCall: makeContractCall, loading: loadingContract } = useApiCall();

  const {
    makeApiCall: makeCalculateCall,
    loading: calculatingPrice,
    error: calculateError,
  } = useApiCall();

  /**
   * Fetch active pricing rules on mount
   */
  useEffect(() => {
    const fetchActivePricing = async () => {
      try {
        const apiConfig = buildApiConfig(endPointEnums.GET_ACTIVE_PRICING);
        const response = await makeApiCall(apiConfig, apiConfig.params);

        if (response?.data) {
          setActivePricing(response.data);
          setPricingLoaded(true);
        }
      } catch (err) {
        console.error('[usePricing] Failed to fetch active pricing:', err);
        // Treat missing pricing (404) or other failures as "no active pricing"
        setActivePricing(null);
        setPricingLoaded(true); // Mark as loaded even on error to prevent infinite loading
      }
    };

    fetchActivePricing();
  }, []);

  /**
   * Fetch active contract for business users
   */
  useEffect(() => {
    if (!isBusinessUser) {
      setContractLoaded(true);
      return;
    }

    const fetchContract = async () => {
      try {
        const apiConfig = buildApiConfig(endPointEnums.GET_MY_CONTRACT);
        const response = await makeContractCall(apiConfig, apiConfig.params);

        if (response?.data) {
          setActiveContract(response.data);
        }
      } catch (err) {
        console.error('[usePricing] Failed to fetch contract:', err);
      } finally {
        setContractLoaded(true);
      }
    };

    fetchContract();
  }, [isBusinessUser]);

  /**
   * Calculate price based on distance, weight, service type, and units (for B2B bulk orders)
   * Auto-routes to V3 for business users with active contracts
   * @param {Object} params - { distance: number, weight: number, service?: string, units?: number, dimensions?: { length, width, height } }
   * @returns {Promise<Object>} - Price calculation result
   */
  const calculatePrice = useCallback(
    async ({
      distance,
      weight,
      service = 'STANDARD',
      units = 1,
      dimensions = null,
      deliveryWindow = null,
    }) => {
      try {
        // Validate inputs
        if (distance == null || distance < 0) {
          console.warn('[usePricing] Invalid distance:', distance);
          return null;
        }
        if (weight == null || weight <= 0) {
          console.warn('[usePricing] Invalid weight:', weight);
          return null;
        }
        if (units < 1) {
          console.warn('[usePricing] Invalid units:', units);
          return null;
        }

        // Use V3 pricing for business users with contracts
        if (isBusinessUser && activeContract) {
          const apiConfig = buildApiConfig(endPointEnums.CALCULATE_PRICE_V3, {
            contractId: activeContract.contract_id || activeContract.contractId,
            userType: 'BUSINESS',
            actualWeightKg: Number(weight),
            distanceKm: Number(distance),
            lengthCm: dimensions?.length || null,
            widthCm: dimensions?.width || null,
            heightCm: dimensions?.height || null,
            deliveryWindow: deliveryWindow,
          });

          const response = await makeCalculateCall(apiConfig, apiConfig.params);

          if (response?.data) {
            return {
              ...response.data,
              pricingEngine: 'V3',
              contractApplied: true,
            };
          }
          return null;
        }

        // Fall back to V2/V1 pricing for individual users
        const apiConfig = buildApiConfig(endPointEnums.CALCULATE_PRICE, {
          distance: Number(distance),
          weight: Number(weight),
          service: service || 'STANDARD',
          units: Number(units), // For B2B bulk ordering
        });

        const response = await makeCalculateCall(apiConfig, apiConfig.params);

        if (response?.data) {
          return {
            ...response.data,
            pricingEngine: 'V1',
            contractApplied: false,
          };
        }

        return null;
      } catch (err) {
        console.error('[usePricing] Price calculation failed:', err);
        return null;
      }
    },
    [makeCalculateCall, isBusinessUser, activeContract]
  );

  /**
   * Get service types from active pricing
   */
  const getServiceTypes = useCallback(() => {
    if (!activePricing || !activePricing.sections) {
      return ['STANDARD'];
    }
    const serviceTypes = activePricing.sections.service_types || [];
    return serviceTypes.length > 0
      ? serviceTypes.map((st) => st.type || st.name || 'STANDARD')
      : ['STANDARD'];
  }, [activePricing]);

  /**
   * Get base fare from active pricing
   */
  const getBaseFare = useCallback(() => {
    if (!activePricing || !activePricing.sections) {
      return 0;
    }
    return activePricing.sections.base_fare || 0;
  }, [activePricing]);

  return {
    activePricing,
    pricingLoaded,
    loadingActivePricing,
    activePricingError,
    calculatePrice,
    calculatingPrice,
    calculateError,
    getServiceTypes,
    getBaseFare,
    // V3 Business pricing
    isBusinessUser,
    activeContract,
    contractLoaded,
    loadingContract,
    hasActiveContract: !!activeContract,
  };
}
